import { useEffect, useRef, useState } from "react";
import { isCancel } from "axios";
import { caseTransformingAxios as axios } from "../../../shared/v2/caseTransformingAxios";

const DAY_IN_THE_LIFE_URL = "/api/v4/day_in_the_life";

const getSections = (opts) => axios.get(DAY_IN_THE_LIFE_URL, opts);
const completeSection = (section) => axios.put(`${DAY_IN_THE_LIFE_URL}/sections/${section}`);
const uncompleteSection = (section) => axios.delete(`${DAY_IN_THE_LIFE_URL}/sections/${section}`);

const useDayInTheLife = ({ enabled }) => {
  const [sections, setSections] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const pendingSections = useRef(new Set());
  const loadId = useRef(0);
  const mutationFailed = useRef(false);

  const applyResponse = (res, { keepError = false } = {}) => {
    setSections(res.data.sections);
    if (!keepError) setError(null);
  };

  const applyError = (err) => {
    if (!isCancel(err)) setError(err.response?.data || err);
  };

  const load = (opts, { keepError = false } = {}) => {
    loadId.current += 1;
    const id = loadId.current;
    const isCurrent = () => id === loadId.current && pendingSections.current.size === 0;

    return getSections(opts)
      .then((res) => {
        if (isCurrent()) applyResponse(res, { keepError });
      })
      .catch((err) => {
        if (isCurrent()) applyError(err);
      });
  };

  useEffect(() => {
    if (!enabled) return undefined;
    const abortController = new AbortController();
    setLoading(true);
    load({ signal: abortController.signal }).finally(() => {
      if (!abortController.signal.aborted) setLoading(false);
    });
    return () => abortController.abort();
  }, [enabled]);

  const setSectionCompleted = (section, completed) => {
    if (pendingSections.current.has(section)) return Promise.resolve();
    if (pendingSections.current.size === 0) mutationFailed.current = false;
    pendingSections.current.add(section);
    loadId.current += 1;

    return (completed ? completeSection(section) : uncompleteSection(section))
      .then((res) => applyResponse(res, { keepError: mutationFailed.current }))
      .catch((err) => {
        mutationFailed.current = true;
        applyError(err);
      })
      .finally(() => {
        pendingSections.current.delete(section);
        if (pendingSections.current.size === 0) return load(undefined, { keepError: mutationFailed.current });
        return undefined;
      });
  };

  return { sections, error, loading, setSectionCompleted };
};

export default useDayInTheLife;
